import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, TouchableWithoutFeedback, Modal, TextInput, ScrollView, Alert, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import RefundDialog from '../../src/components/RefundDialog';

/* ── Types ── */

interface OrderItem {
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceInCents: number;
}

interface HardwareOrder {
  readonly id: string;
  readonly ownerBusinessName: string;
  readonly ownerEmail?: string;
  readonly ownerPhone?: string | null;
  readonly ownerAccountNumber?: string | null;
  readonly items: OrderItem[];
  readonly shippingCostInCents: number;
  readonly totalInCents: number;
  readonly status: string;
  readonly createdAt: string;
  readonly trackingNumber?: string;
  readonly carrier?: string;
  readonly notes?: string;
  readonly customerMessage?: string;
  readonly refundedAt?: string | null;
  readonly refundedAmount?: number;
  readonly stripePaymentIntentId?: string;
  readonly shippingName?: string;
  readonly shippingLine1?: string;
  readonly shippingLine2?: string;
  readonly shippingCity?: string;
  readonly shippingState?: string;
  readonly shippingZip?: string;
  readonly shippingCountry?: string;
}

interface OrderStats {
  pending: number;
  confirmed: number;
  shipped: number;
  delivered: number;
  cancelled: number;
}

/* ── Constants ── */

const STATUS_FILTERS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Cancelled', value: 'cancelled' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  processing: '#f59e0b',
  confirmed: '#2563eb',
  shipped: '#8b5cf6',
  delivered: '#059669',
  cancelled: '#dc2626',
};

/** Valid next statuses from a given status */
const NEXT_STATUSES: Record<string, string[]> = {
  pending: ['confirmed', 'cancelled'],
  processing: ['confirmed', 'cancelled'],
  confirmed: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: [],
};

const statusColor = (s: string) => STATUS_COLORS[s] || '#6b7280';

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const shortId = (id: string) => id.slice(0, 8).toUpperCase();

const itemsSummary = (items: OrderItem[]) =>
  items.map(i => `${i.quantity}x ${i.productName}`).join(', ');

const PAGE_LIMIT = 20;

/* ── Component ── */

export default function AdminHardwareOrders() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [copiedField, setCopiedField] = useState('');

  const copyText = async (text: string, field: string) => {
    try {
      if (Platform.OS === 'web') await navigator.clipboard.writeText(text);
      else await Clipboard.setStringAsync(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(f => f === field ? '' : f), 1500);
    } catch {}
  };

  const CopyBtn = ({ text, field }: { text: string; field: string }) => (
    <TouchableOpacity onPress={() => copyText(text, field)} hitSlop={8} style={styles.copyBtn}>
      <Ionicons name={copiedField === field ? 'checkmark' : 'copy-outline'} size={14} color={copiedField === field ? '#059669' : colors.textLight} />
    </TouchableOpacity>
  );

  const [orders, setOrders] = useState<HardwareOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState<OrderStats | null>(null);

  /* Modal state */
  const [selectedOrder, setSelectedOrder] = useState<HardwareOrder | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [updating, setUpdating] = useState(false);

  /* Refund state */
  const [refunding, setRefunding] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);

  /* ── Fetch orders ── */
  const fetchData = useCallback(async (p = 1, s = '', status = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(PAGE_LIMIT) });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      const data = await api<{ orders: HardwareOrder[]; total: number }>(`/api/admin/hardware-orders?${params}`);
      if (signal?.aborted) return;
      setOrders(data.orders);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  /* ── Fetch stats ── */
  const fetchStats = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await api<OrderStats>('/api/admin/hardware-orders/stats');
      if (signal?.aborted) return;
      setStats(data);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchData(1, '', '', ac.signal);
    fetchStats(ac.signal);
    return () => ac.abort();
  }, []);

  /* ── Open detail modal ── */
  const openDetail = (order: HardwareOrder) => {
    setSelectedOrder(order);
    const nextStatuses = NEXT_STATUSES[order.status] || [];
    setUpdateStatus(nextStatuses[0] || '');
    setTrackingNumber(order.trackingNumber || '');
    setCarrier(order.carrier || '');
    setUpdateNotes('');
    setCustomerMessage('');
    setShowRefundConfirm(false);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedOrder(null);
    setShowRefundConfirm(false);
  };

  /* ── Update status ── */
  const handleUpdateStatus = async () => {
    if (!selectedOrder || !updateStatus) return;
    setUpdating(true);
    try {
      const body: Record<string, string> = { status: updateStatus };
      if (updateStatus === 'shipped') {
        if (trackingNumber) body.trackingNumber = trackingNumber;
        if (carrier) body.carrier = carrier;
      }
      if (updateNotes) body.notes = updateNotes;
      if (customerMessage) body.customerMessage = customerMessage;
      await api(`/api/admin/hardware-orders/${selectedOrder.id}/status`, { method: 'PUT', body });
      closeModal();
      fetchData(page, search, statusFilter);
      fetchStats();
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  /* ── Refund ── */
  const handleRefund = async (amount: number) => {
    if (!selectedOrder) return;
    setRefunding(true);
    try {
      await api(`/api/admin/hardware-orders/${selectedOrder.id}/refund`, { method: 'POST', body: { amount } });
      setShowRefundConfirm(false);
      closeModal();
      fetchData(page, search, statusFilter);
      fetchStats();
    } catch (err) { console.error(err); }
    finally { setRefunding(false); }
  };

  /* ── Stats tap → filter ── */
  const handleStatTap = (status: string) => {
    const next = statusFilter === status ? '' : status;
    setStatusFilter(next);
    fetchData(1, search, next);
  };

  const csvEndpoint = `/api/admin/hardware-orders?${new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }) })}`;

  /* ── Stats cards ── */
  const renderStats = () => {
    if (!stats) return null;
    const items: { label: string; key: keyof OrderStats; color: string }[] = [
      { label: 'Pending', key: 'pending', color: STATUS_COLORS.pending },
      { label: 'Confirmed', key: 'confirmed', color: STATUS_COLORS.confirmed },
      { label: 'Shipped', key: 'shipped', color: STATUS_COLORS.shipped },
      { label: 'Delivered', key: 'delivered', color: STATUS_COLORS.delivered },
      { label: 'Cancelled', key: 'cancelled', color: STATUS_COLORS.cancelled },
    ];
    return (
      <View style={styles.statsRow}>
        {items.map(({ label, key, color }) => (
          <TouchableOpacity
            key={key}
            style={[styles.statCard, { borderColor: statusFilter === key ? color : colors.border }]}
            onPress={() => handleStatTap(key)}
            activeOpacity={0.7}
          >
            <Text style={[styles.statCount, { color }]}>{stats[key]}</Text>
            <Text style={styles.statLabel}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  /* ── Order row ── */
  const renderItem = ({ item }: { item: HardwareOrder }) => {
    const sc = statusColor(item.status);
    const refundedSoFar = item.refundedAmount ?? 0;
    const fullyRefunded = !!item.refundedAt || (refundedSoFar > 0 && refundedSoFar >= item.totalInCents);
    const partiallyRefunded = refundedSoFar > 0 && !fullyRefunded;
    return (
      <TouchableOpacity style={styles.row} onPress={() => openDetail(item)} activeOpacity={0.7}>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text style={styles.name}>{shortId(item.id)}</Text>
              {fullyRefunded ? (
                <View style={[styles.badge, { backgroundColor: '#dc262615' }]}>
                  <Text style={[styles.badgeText, { color: '#dc2626' }]}>Refunded {fmt(refundedSoFar)}</Text>
                </View>
              ) : partiallyRefunded ? (
                <View style={[styles.badge, { backgroundColor: '#d9770615' }]}>
                  <Text style={[styles.badgeText, { color: '#d97706' }]}>Partial refund {fmt(refundedSoFar)} / {fmt(item.totalInCents)}</Text>
                </View>
              ) : null}
            </View>
            <View style={[styles.badge, { backgroundColor: sc + '15' }]}>
              <Text style={[styles.badgeText, { color: sc }]}>{item.status}</Text>
            </View>
          </View>
          <Text style={styles.meta}>{item.ownerBusinessName} · {itemsSummary(item.items)} · {fmt(item.totalInCents)}</Text>
          {(item.ownerEmail || item.ownerPhone || item.ownerAccountNumber) ? (
            <Text style={styles.sub}>
              {item.ownerEmail || ''}
              {item.ownerAccountNumber ? ` · ${item.ownerAccountNumber}` : ''}
              {item.ownerPhone ? ` · ${item.ownerPhone}` : ''}
            </Text>
          ) : null}
          <Text style={styles.sub}>
            {new Date(item.createdAt).toLocaleDateString()}
            {item.trackingNumber ? ` · Tracking: ${item.trackingNumber}` : ''}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  /* ── Detail modal ── */
  const renderModal = () => {
    if (!selectedOrder) return null;
    const order = selectedOrder;
    const sc = statusColor(order.status);
    const nextStatuses = NEXT_STATUSES[order.status] || [];
    const hasAddr = order.shippingLine1;
    const refundedSoFar = order.refundedAmount ?? 0;
    const remainingRefundable = Math.max(0, order.totalInCents - refundedSoFar);
    const canRefund = !!order.stripePaymentIntentId && remainingRefundable > 0;

    return (
      <Modal visible={modalVisible} animationType="slide" transparent onRequestClose={closeModal}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback onPress={closeModal}>
            <View style={StyleSheet.absoluteFill} />
          </TouchableWithoutFeedback>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <ScrollView showsVerticalScrollIndicator nestedScrollEnabled>
              {/* Header */}
              <View style={styles.modalHeader}>
                <View style={styles.copyRow}>
                  <Text selectable style={styles.modalTitle}>Order {shortId(order.id)}</Text>
                  <CopyBtn text={order.id} field="id" />
                </View>
                <TouchableOpacity onPress={closeModal} hitSlop={12}>
                  <Ionicons name="close" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>

              {/* Status + refund badges */}
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: spacing.md }}>
                <View style={[styles.badge, { backgroundColor: sc + '15' }]}>
                  <Text style={[styles.badgeText, { color: sc }]}>{order.status}</Text>
                </View>
                {order.refundedAt ? (
                  <View style={[styles.badge, { backgroundColor: '#dc262615' }]}>
                    <Text style={[styles.badgeText, { color: '#dc2626' }]}>Refunded {new Date(order.refundedAt).toLocaleDateString()}</Text>
                  </View>
                ) : refundedSoFar > 0 ? (
                  <View style={[styles.badge, { backgroundColor: '#d9770615' }]}>
                    <Text style={[styles.badgeText, { color: '#d97706' }]}>{fmt(refundedSoFar)} refunded</Text>
                  </View>
                ) : null}
              </View>

              {/* Info */}
              <Text style={styles.modalLabel}>Business</Text>
              <View style={styles.copyRow}>
                <Text selectable style={styles.modalValue}>{order.ownerBusinessName}</Text>
                <CopyBtn text={order.ownerBusinessName} field="biz" />
              </View>

              <Text style={styles.modalLabel}>Date</Text>
              <View style={styles.copyRow}>
                <Text selectable style={styles.modalValue}>{new Date(order.createdAt).toLocaleString()}</Text>
                <CopyBtn text={new Date(order.createdAt).toLocaleString()} field="date" />
              </View>

              <Text style={styles.modalLabel}>Subtotal</Text>
              <View style={styles.copyRow}>
                <Text selectable style={styles.modalValue}>{fmt(order.totalInCents - (order.shippingCostInCents || 0))}</Text>
              </View>

              <Text style={styles.modalLabel}>Shipping</Text>
              <View style={styles.copyRow}>
                <Text selectable style={styles.modalValue}>{fmt(order.shippingCostInCents || 0)}</Text>
              </View>

              <Text style={styles.modalLabel}>Total</Text>
              <View style={styles.copyRow}>
                <Text selectable style={styles.modalValue}>{fmt(order.totalInCents)}</Text>
                <CopyBtn text={fmt(order.totalInCents)} field="total" />
              </View>

              {order.trackingNumber ? (
                <>
                  <Text style={styles.modalLabel}>Tracking</Text>
                  <View style={styles.copyRow}>
                    <Text selectable style={styles.modalValue}>{order.carrier ? `${order.carrier}: ` : ''}{order.trackingNumber}</Text>
                    <CopyBtn text={order.trackingNumber} field="tracking" />
                  </View>
                </>
              ) : null}

              {/* Admin notes (read-only display of existing notes) */}
              {order.notes ? (
                <>
                  <Text style={styles.modalLabel}>Admin Notes</Text>
                  <View style={styles.copyRow}>
                    <Text selectable style={[styles.modalValue, { fontStyle: 'italic' }]}>{order.notes}</Text>
                    <CopyBtn text={order.notes} field="notes" />
                  </View>
                </>
              ) : null}

              {/* Shipping address */}
              {hasAddr ? (
                <>
                  <Text style={styles.modalLabel}>Shipping Address</Text>
                  <View style={styles.copyRow}>
                    <Text selectable style={styles.modalValue}>
                      {[order.shippingName, order.shippingLine1, order.shippingLine2, [order.shippingCity, order.shippingState, order.shippingZip].filter(Boolean).join(', '), order.shippingCountry].filter(Boolean).join('\n')}
                    </Text>
                    <CopyBtn text={[order.shippingName, order.shippingLine1, order.shippingLine2, [order.shippingCity, order.shippingState, order.shippingZip].filter(Boolean).join(', '), order.shippingCountry].filter(Boolean).join('\n')} field="addr" />
                  </View>
                </>
              ) : null}

              {/* Items — card-style rows */}
              <Text style={[styles.modalLabel, { marginTop: spacing.md }]}>Items</Text>
              <View style={styles.itemsTable}>
                <View style={styles.itemsHeaderRow}>
                  <Text style={[styles.itemsHeaderCell, { flex: 3 }]}>ITEM</Text>
                  <Text style={[styles.itemsHeaderCell, { flex: 2, textAlign: 'right' }]}>QTY × PRICE</Text>
                  <Text style={[styles.itemsHeaderCell, { flex: 1, textAlign: 'right' }]}>TOTAL</Text>
                </View>
                {order.items.map((item, idx) => (
                  <View key={idx} style={[styles.itemsRow, idx % 2 === 0 && { backgroundColor: colors.background + '80' }]}>
                    <Text selectable style={[styles.itemsCell, { flex: 3 }]}>{item.productName}</Text>
                    <Text selectable style={[styles.itemsCell, { flex: 2, textAlign: 'right', color: colors.textSecondary }]}>
                      {item.quantity} × {fmt(item.priceInCents)}
                    </Text>
                    <Text selectable style={[styles.itemsCell, { flex: 1, textAlign: 'right', fontWeight: fontWeight.semibold }]}>
                      {fmt(item.priceInCents * item.quantity)}
                    </Text>
                  </View>
                ))}
              </View>

              {/* Refund button — available for any paid order with remaining refundable balance */}
              {canRefund ? (
                <View style={{ marginTop: spacing.md }}>
                  {showRefundConfirm ? (
                    <RefundDialog
                      visible={true}
                      originalCents={order.totalInCents}
                      alreadyRefundedCents={refundedSoFar}
                      subtitle="Refund hardware order"
                      loading={refunding}
                      onCancel={() => setShowRefundConfirm(false)}
                      onConfirm={(amount) => handleRefund(amount)}
                    />
                  ) : (
                    <TouchableOpacity style={styles.refundBtn} onPress={() => setShowRefundConfirm(true)}>
                      <Ionicons name="card-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
                      <Text style={styles.refundBtnText}>Refund Order</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ) : null}

              {/* Status update form */}
              {nextStatuses.length > 0 ? (
                <View style={styles.updateSection}>
                  <Text style={[styles.modalLabel, { marginBottom: spacing.sm }]}>Update Status</Text>

                  {/* Status picker chips */}
                  <View style={styles.statusPickerRow}>
                    {nextStatuses.map(s => (
                      <TouchableOpacity
                        key={s}
                        style={[styles.statusChip, updateStatus === s && { backgroundColor: statusColor(s), borderColor: statusColor(s) }]}
                        onPress={() => setUpdateStatus(s)}
                      >
                        <Text style={[styles.statusChipText, updateStatus === s && { color: '#fff' }]}>{s}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  {/* Tracking fields when shipped */}
                  {updateStatus === 'shipped' ? (
                    <View style={styles.trackingFields}>
                      <TextInput
                        style={styles.formInput}
                        placeholder="Tracking number"
                        placeholderTextColor={colors.textLight}
                        value={trackingNumber}
                        onChangeText={setTrackingNumber}
                      />
                      <TextInput
                        style={styles.formInput}
                        placeholder="Carrier (e.g. UPS, FedEx)"
                        placeholderTextColor={colors.textLight}
                        value={carrier}
                        onChangeText={setCarrier}
                      />
                    </View>
                  ) : null}

                  {/* Internal notes */}
                  <TextInput
                    style={[styles.formInput, { minHeight: 60, textAlignVertical: 'top' }]}
                    placeholder="Internal notes (admin only, not emailed)"
                    placeholderTextColor={colors.textLight}
                    value={updateNotes}
                    onChangeText={setUpdateNotes}
                    multiline
                  />

                  {/* Customer message */}
                  <TextInput
                    style={[styles.formInput, { minHeight: 60, textAlignVertical: 'top' }]}
                    placeholder="Customer message (included in status email)"
                    placeholderTextColor={colors.textLight}
                    value={customerMessage}
                    onChangeText={setCustomerMessage}
                    multiline
                  />

                  {/* Submit */}
                  <TouchableOpacity
                    style={[styles.updateBtn, !updateStatus && { opacity: 0.5 }]}
                    onPress={handleUpdateStatus}
                    disabled={!updateStatus || updating}
                  >
                    <Text style={styles.updateBtnText}>{updating ? 'Updating...' : `Update to ${updateStatus}`}</Text>
                  </TouchableOpacity>
                </View>
              ) : null}

              {/* Close button at bottom */}
              <TouchableOpacity style={styles.closeBtn} onPress={closeModal}>
                <Text style={styles.closeBtnText}>Close</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      {renderStats()}
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search, statusFilter)}
        total={total} page={page} limit={PAGE_LIMIT} onPageChange={(p) => fetchData(p, search, statusFilter)}
        csvEndpoint={csvEndpoint} csvFilename="hardware-orders.csv" label="order"
        filterOptions={STATUS_FILTERS} activeFilter={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); fetchData(1, search, v); }}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={orders} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No hardware orders found</Text>}
        />
      )}
      {renderModal()}
      </ContentContainer>
    </View>
  );
}

/* ── Styles ── */

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },

  /* Stats */
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md },
  statCard: { flex: 1, minWidth: 90, backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.border, padding: spacing.sm, alignItems: 'center' },
  statCount: { fontSize: fontSize.lg, fontWeight: fontWeight.bold },
  statLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },

  /* List rows */
  row: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, textTransform: 'capitalize' },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },

  /* Modal */
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  modalContent: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: 540, maxHeight: '90%', elevation: 5 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  modalLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase', letterSpacing: 0.5 },
  modalValue: { fontSize: fontSize.sm, color: c.text, marginTop: 2, flex: 1 },
  copyRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 6 },
  copyBtn: { paddingTop: 3 },

  /* Items table — card-style */
  itemsTable: { marginTop: spacing.xs, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.sm, overflow: 'hidden' },
  itemsHeaderRow: { flexDirection: 'row', backgroundColor: c.background, paddingHorizontal: spacing.sm, paddingVertical: 10 },
  itemsHeaderCell: { fontSize: fontSize.xs, fontWeight: fontWeight.bold, color: c.textSecondary, letterSpacing: 0.5 },
  itemsRow: { flexDirection: 'row', paddingHorizontal: spacing.sm, paddingVertical: 10, borderTopWidth: 1, borderTopColor: c.border },
  itemsCell: { fontSize: fontSize.sm, color: c.text },

  /* Update form */
  updateSection: { marginTop: spacing.lg, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.border },
  statusPickerRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  statusChip: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: c.border, backgroundColor: c.background },
  statusChipText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.textSecondary, textTransform: 'capitalize' },
  trackingFields: { gap: spacing.sm, marginBottom: spacing.sm },
  formInput: { backgroundColor: c.background, borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, fontSize: fontSize.sm, color: c.text, marginBottom: spacing.sm },
  updateBtn: { backgroundColor: c.primary, borderRadius: borderRadius.md, paddingVertical: spacing.sm, alignItems: 'center', marginTop: spacing.xs },
  updateBtnText: { color: c.white, fontSize: fontSize.sm, fontWeight: fontWeight.semibold },

  /* Refund */
  refundBtn: { backgroundColor: '#dc2626', borderRadius: borderRadius.md, paddingVertical: spacing.sm, alignItems: 'center', flexDirection: 'row', justifyContent: 'center' },
  refundBtnText: { color: '#fff', fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  cancelRefundBtn: { backgroundColor: c.background, borderRadius: borderRadius.md, paddingVertical: spacing.sm, alignItems: 'center', borderWidth: 1, borderColor: c.border },
  cancelRefundBtnText: { color: c.text, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
  refundConfirm: { backgroundColor: '#dc262610', borderRadius: borderRadius.md, padding: spacing.md },

  /* Close button */
  closeBtn: { marginTop: spacing.lg, paddingVertical: spacing.sm, alignItems: 'center', borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md },
  closeBtnText: { color: c.textSecondary, fontSize: fontSize.sm, fontWeight: fontWeight.medium },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
